import React, { forwardRef, useEffect, useRef } from 'react';
import './UICheckbox.scss';

export interface UICheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /**
   * Стан вибору чекбокса
   */
  checked?: boolean;

  /**
   * Обробник зміни стану
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

  /**
   * Варіант стилю чекбокса
   */
  variant?: 'default' | 'success' | 'error';

  /**
   * Розмір чекбокса
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Кастомна ширина чекбокса в % (переопределяет стандартные размеры)
   */
  customWidth?: number;

  /**
   * Текст лейблу поруч з чекбоксом
   */
  label?: string;

  /**
   * Частково вибраний стан (indeterminate)
   */
  indeterminate?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Ім'я для групування чекбоксів
   */
  name?: string;

  /**
   * Значення чекбокса
   */
  value?: string;

  /**
   * Заблокований стан
   */
  disabled?: boolean;
}

// Моковые данные для демонстрації
const mockProps: Partial<UICheckboxProps> = {
  label: '',
  size: 'medium',
  variant: 'default',
  checked: false,
  disabled: false,
  indeterminate: false,
};

const baseClass = 'UICheckbox';

const UICheckbox = forwardRef<HTMLInputElement, UICheckboxProps>(
  (
    {
      checked = mockProps.checked,
      onChange = () => {},
      variant = mockProps.variant,
      size = mockProps.size,
      customWidth,
      label = mockProps.label,
      indeterminate = mockProps.indeterminate,
      className = '',
      name,
      value,
      disabled = mockProps.disabled,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const combinedRef = (ref as React.RefObject<HTMLInputElement>) || inputRef;

    // Встановлюємо indeterminate стан через DOM API
    useEffect(() => {
      if (combinedRef.current) {
        combinedRef.current.indeterminate = indeterminate || false;
      }
    }, [indeterminate, combinedRef]);

    const containerClasses = [
      baseClass,
      `${baseClass}--${size}`,
      `${baseClass}--${variant}`,
      indeterminate && `${baseClass}--indeterminate`,
      disabled && `${baseClass}--disabled`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        // Скидаємо indeterminate при зміні
        if (combinedRef.current) {
          combinedRef.current.indeterminate = false;
        }
        onChange?.(event);
      }
    };

    // Стиль для кастомної ширини
    const customStyle = customWidth ? { width: `${customWidth}%` } : {};

    return (
      <label className={containerClasses} style={customStyle}>
        <input
          ref={combinedRef}
          type="checkbox"
          className={`${baseClass}__input`}
          checked={checked}
          onChange={handleChange}
          name={name}
          value={value}
          disabled={disabled}
          {...props}
        />
        <span className={`${baseClass}__box`} />
        {label && <span className={`${baseClass}__label`}>{label}</span>}
      </label>
    );
  },
);

UICheckbox.displayName = 'UICheckbox';

export default UICheckbox;